import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { jwtTokenService } from '@mathflat/shared/@modules/auth/jwtTokenService'
import axios, { HttpStatusCode } from 'axios'
import qs from 'qs'

import { TOAST_STRING } from '~/@common/constants/strings'
import { errorHandlerService, toastService } from '~/@common/services'
import { envUtils } from '~/@common/utils/envUtils.ts'

import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from './jwtInterceptors'

const BASE_URL = (() => {
  if (envUtils.isStaging) {
    return `https://ai-staging.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isLive) {
    return `https://ai.${domainProvider.TARGET_DOMAIN}`
  }
  return localStorage.getItem('DEV_API') ?? `https://ai-dev.${domainProvider.TARGET_DOMAIN}`
})()

const chatAxios = axios.create({
  baseURL: BASE_URL,
  headers: {
    common: {
      'x-platform': 'STUDENT',
    },
    post: { 'Content-Type': 'application/json' },
  },
  paramsSerializer: {
    serialize: (params) => {
      return qs.stringify(params, { arrayFormat: 'comma' })
    },
  },
})

chatAxios.interceptors.request.use(wrapRequestInterceptorWithJwt((config) => config))

chatAxios.interceptors.response.use(
  (response) => response,
  wrapErrorHandlerWithJwt(
    async (error: unknown) => {
      // 인터넷 연결을 보장하지 못함 (https://developer.mozilla.org/en-US/docs/Web/API/Navigator/onLine)
      // navigator.onLine이 undefined일 수 있어서 false와 비교
      if (navigator.onLine === false) {
        toastService.error(TOAST_STRING.networkOffline)
        return Promise.reject(error)
      }

      const axiosError = error as { response?: { status?: number } }
      const status = axiosError?.response?.status

      if (!status) {
        errorHandlerService.handle(error as Error)
        return Promise.reject(error)
      }

      if (status === HttpStatusCode.NotFound) {
        return error
      }

      errorHandlerService.handle(error as Error, {
        logType: 'error',
        useRemoteLogging: false,
      })

      return Promise.reject(error)
    },
    async (originalRequest, newAccessToken) => {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return chatAxios(originalRequest)
    },
  ),
)

export default chatAxios

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchSSE = (url: string, options: { params?: Record<string, any> } & RequestInit) => {
  const { params, ...otherOptions } = options

  const accessToken = jwtTokenService.getAccessToken()
  const queryString = params
    ? `?${qs.stringify(params, {
        arrayFormat: 'comma',
        skipNulls: true,
        encoder: (str, defaultEncoder, charset, type) => {
          if (
            (type === 'value' && typeof str === 'string' && str.startsWith('http://')) ||
            str.startsWith('https://')
          ) {
            return str
          }
          return defaultEncoder(str, defaultEncoder, charset)
        },
      })}`
    : ''

  return fetch(`${BASE_URL}${url}${queryString}`, {
    method: 'POST',
    headers: {
      'x-platform': 'STUDENT',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json; charset=utf-8',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    ...otherOptions,
  })
}
